import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../lib/apiClient'
import { useLienClient } from '../../contexts/ClientAfficheContext'
import type { SearchResponse } from '../../../solutions/finops/types/licences'
import { Button } from '@/platform/components/ui/button'
import { Input } from '@/platform/components/ui/input'

interface QuickSearchProps {
  selectedAccountId: string | null
}

export function QuickSearch({ selectedAccountId }: QuickSearchProps) {
  const navigate = useNavigate()
  const lien = useLienClient()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResponse | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    setResults(null)
    setQuery('')
  }, [])

  // Fermeture sur clic extérieur
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close()
      }
    }
    if (open) document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open, close])

  // Fermeture sur Échap
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    if (open) document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, close])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setQuery(val)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (val.length < 3 || !selectedAccountId) {
      setResults(null)
      setOpen(false)
      return
    }

    debounceRef.current = setTimeout(() => {
      setLoading(true)
      apiFetch<SearchResponse>(
        `/api/search?accountId=${encodeURIComponent(selectedAccountId)}&q=${encodeURIComponent(val)}`
      )
        .then(res => {
          setResults(res)
          setOpen(true)
        })
        .catch(() => {
          setResults(null)
        })
        .finally(() => setLoading(false))
    }, 300)
  }

  function handleSelectLicense(skuId: string) {
    navigate(lien(`/licences/${skuId}`))
    close()
  }

  function handleSelectUser(objectId: string) {
    navigate(lien(`/licences-utilisateurs/${objectId}`))
    close()
  }

  const hasResults = results && (results.licenses.length > 0 || results.users.length > 0)
  const showDropdown = open && query.length >= 3

  return (
    <div ref={containerRef} style={{ position: 'relative', width: 220, flexShrink: 1, minWidth: 100 }}>
      {/* Input */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'hsl(var(--background))',
        border: `1px solid ${open ? 'hsl(var(--accent))' : 'hsl(var(--border))'}`,
        borderRadius: 3,
        padding: '5px 10px',
      }}>
        {loading ? (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" strokeWidth="1.5" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0, stroke: 'hsl(var(--accent))' }}>
            <circle cx="8" cy="8" r="6" strokeDasharray="20 8" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" strokeWidth="1.5" style={{ flexShrink: 0, stroke: 'hsl(var(--foreground))' }}>
            <circle cx="7" cy="7" r="5"/>
            <path d="M11 11l3 3"/>
          </svg>
        )}
        <Input
          value={query}
          onChange={handleChange}
          placeholder="Rechercher…"
          className="border-none bg-transparent p-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 text-small text-primary w-full"
        />
        {query.length > 0 && (
          <Button
            variant="ghost"
            onClick={close}
            className="flex-shrink-0 h-auto p-0 text-foreground leading-none hover:bg-transparent"
          >
            ×
          </Button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: 'hsl(var(--card))',
          border: '1px solid hsl(var(--border))',
          borderRadius: 3,
          zIndex: 200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxHeight: 360,
          overflowY: 'auto',
          minWidth: 280,
        }}>
          {!hasResults ? (
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-small)', color: 'hsl(var(--foreground))', padding: '12px 14px', textAlign: 'center' }}>
              Aucun résultat
            </div>
          ) : (
            <>
              {results!.licenses.length > 0 && (
                <div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-caption)', fontWeight: 'bold', color: 'hsl(var(--foreground))', padding: '8px 14px 4px', borderBottom: '1px solid hsl(var(--muted))' }}>
                    Licences
                  </div>
                  {results!.licenses.map(l => (
                    <button
                      key={l.skuId}
                      onClick={() => handleSelectLicense(l.skuId)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 14px',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid hsl(var(--background))',
                        cursor: 'pointer',
                      }}
                      onMouseOver={e => (e.currentTarget.style.background = 'hsl(var(--muted))')}
                      onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-small)', color: 'hsl(var(--primary))', fontWeight: 600 }}>{l.skuTitle}</div>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-caption)', color: 'hsl(var(--foreground))', marginTop: 1 }}>
                        {l.skuPartNumber}{l.productId ? ` · ${l.productId}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {results!.users.length > 0 && (
                <div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-caption)', fontWeight: 'bold', color: 'hsl(var(--foreground))', padding: '8px 14px 4px', borderBottom: '1px solid hsl(var(--muted))' }}>
                    Utilisateurs
                  </div>
                  {results!.users.map(u => (
                    <button
                      key={u.objectId}
                      onClick={() => handleSelectUser(u.objectId)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 14px',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid hsl(var(--background))',
                        cursor: 'pointer',
                      }}
                      onMouseOver={e => (e.currentTarget.style.background = 'hsl(var(--muted))')}
                      onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-small)', color: 'hsl(var(--primary))', fontWeight: 600 }}>{u.displayName}</div>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-caption)', color: 'hsl(var(--foreground))', marginTop: 1 }}>{u.upn}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
